/**
 * Forgot-password request screen (T27.2.10.1 Phase B, E1/E2).
 *
 * Asks for an email and calls `useRequestReset` (→ POST /api/auth/request-reset).
 * The response is non-enumerating — the SAME generic confirmation whether or not
 * the email has an account — so on success this always flips to "if an account
 * exists, we sent a link", never revealing whether the address is registered.
 *
 * Reachable from the login screen ("Forgot password?") and from the
 * account-exists email's inline `/reset` link. Mirrors the LoginPage card layout.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useRequestReset } from '@shopista/shared/api/hooks';
import { apiErrorMessage, ApiError } from '@shopista/shared/api';
import { Button, Card, Input, Logo } from '../../components';
import { routes } from '../../routes/paths';
import {
  authPageStyle,
  authStackStyle,
  authWordmarkRowStyle,
  authCardBodyStyle,
  authHeadingStyle,
  authSubheadingStyle,
  authFormStyle,
  authInlineNoteStyle,
  authInlineErrorStyle,
  authFooterRowStyle,
  authLinkStyle,
  EMAIL_PATTERN,
} from './authLayout';

const INVALID_EMAIL_MESSAGE = 'Please enter a valid email address.';

export function ResetRequestPage(): JSX.Element {
  const requestReset = useRequestReset();
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();
      setFormError(null);
      // Trim before validating + sending — mobile keyboards append a trailing
      // space after an email autofill, failing the pattern on a valid address.
      const normalizedEmail = email.trim();
      if (!EMAIL_PATTERN.test(normalizedEmail)) {
        setFormError(INVALID_EMAIL_MESSAGE);
        return;
      }
      requestReset.mutate(
        { email: normalizedEmail },
        {
          onSuccess: () => setSubmitted(true),
          onError: (err) => {
            if (err instanceof ApiError && err.status === 429) {
              setFormError('Too many requests. Please try again later.');
              return;
            }
            // Any other failure still shouldn't leak existence — show a generic
            // confirmation rather than an error that hints the email is known.
            setSubmitted(true);
            void apiErrorMessage(err, '');
          },
        },
      );
    },
    [email, requestReset],
  );

  return (
    <main style={authPageStyle} data-testid="reset-request-page">
      <div style={authStackStyle}>
        <div style={authWordmarkRowStyle}>
          <Logo size={80} showText data-testid="reset-request-logo" />
        </div>
        <Card padding="lg" shadow="md" data-testid="reset-request-card">
          <div style={authCardBodyStyle}>
            {submitted ? (
              <>
                <h1 style={authHeadingStyle}>Check your inbox</h1>
                <p style={authSubheadingStyle} data-testid="reset-request-sent">
                  If an account exists for that email, we sent a link to reset the
                  password. It works for 1 hour.
                </p>
                <p style={authInlineNoteStyle}>
                  <Link to={routes.login} style={authLinkStyle} data-testid="reset-request-back">
                    Back to sign in
                  </Link>
                </p>
              </>
            ) : (
              <>
                <h1 style={authHeadingStyle}>Reset your password</h1>
                <p style={authSubheadingStyle}>
                  Enter your email and we'll send a link to set a new password.
                </p>
                <form
                  style={authFormStyle}
                  onSubmit={onSubmit}
                  data-testid="reset-request-form"
                  noValidate
                >
                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="reset-request-email"
                    required
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={requestReset.isPending}
                    data-testid="reset-request-submit"
                  >
                    {requestReset.isPending ? 'Sending…' : 'Send reset link'}
                  </Button>
                  {formError && (
                    <p
                      role="alert"
                      data-testid="reset-request-error"
                      style={authInlineErrorStyle}
                    >
                      {formError}
                    </p>
                  )}
                </form>
              </>
            )}
          </div>
        </Card>
        <p style={authFooterRowStyle}>
          <Link to={routes.login} style={authLinkStyle} data-testid="reset-request-signin-link">
            Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
